<template>
  <div class="map-tools">
    <div class="absolute top-4 right-4 z-10 flex flex-col space-y-2">
      <!-- Tool buttons -->
      <button 
        @click="showStyler = !showStyler"
        class="bg-white p-2 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
        :class="{ 'bg-primary-50 text-primary-700': showStyler }"
        title="Style Properties"
      >
        <i class="fas fa-palette text-lg"></i>
      </button>
      
      <button 
        @click="showLayers = !showLayers"
        class="bg-white p-2 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
        :class="{ 'bg-primary-50 text-primary-700': showLayers }"
        title="Layer Controls"
      >
        <i class="fas fa-layer-group text-lg"></i>
      </button>
      
      <button 
        @click="resetView"
        class="bg-white p-2 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
        title="Reset View"
      >
        <i class="fas fa-home text-lg"></i>
      </button>
      
      <button 
        @click="toggleSelection"
        class="bg-white p-2 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
        :class="{ 'bg-primary-50 text-primary-700': isSelectionActive }"
        title="Select Properties"
      >
        <i class="fas fa-object-group text-lg"></i>
      </button>
      
      <button 
        @click="showMeasure = !showMeasure"
        class="bg-white p-2 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
        :class="{ 'bg-primary-50 text-primary-700': showMeasure }"
        title="Measurement Tools"
      >
        <i class="fas fa-ruler-combined text-lg"></i>
      </button>
      
      <button 
        @click="showExport = !showExport"
        class="bg-white p-2 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
        :class="{ 'bg-primary-50 text-primary-700': showExport }"
        title="Export Map"
      >
        <i class="fas fa-file-export text-lg"></i>
      </button>
    </div>
    
    <!-- Styler Panel -->
    <div 
      v-if="showStyler"
      class="absolute top-4 right-16 z-10 w-72 transform transition-all duration-300"
      :class="{ 'translate-x-0 opacity-100': showStyler, 'translate-x-8 opacity-0': !showStyler }"
    >
      <MapStyler 
        :selected-properties="selectedProperties"
        @style-applied="onStyleApplied"
        @style-created="onStyleCreated"
        @style-updated="onStyleUpdated"
      />
    </div>
    
    <!-- Layers Panel -->
    <div 
      v-if="showLayers"
      class="absolute top-4 right-16 z-10 w-72 transform transition-all duration-300"
      :class="{ 'translate-x-0 opacity-100': showLayers, 'translate-x-8 opacity-0': !showLayers }"
    >
      <div class="p-4 bg-white rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-4">Layers</h3>
        
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <label class="flex items-center">
              <input 
                v-model="layerVisibility.baseMap" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">Base Map</span>
            </label>
            <select 
              v-model="baseMapType"
              class="text-sm p-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="satellite">Satellite</option>
              <option value="osm">OpenStreetMap</option>
              <option value="topo">Topographic</option>
              <option value="street">Street Map</option>
              <option value="terrain">Terrain</option>
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
          </div>
          
          <div class="flex items-center justify-between">
            <label class="flex items-center">
              <input 
                v-model="layerVisibility.properties" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">Properties</span>
            </label>
            <span class="text-xs text-gray-500">{{ propertyCount }} items</span>
          </div>
          
          <div class="flex items-center justify-between">
            <label class="flex items-center">
              <input 
                v-model="layerVisibility.heatmap" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">Heatmap</span>
            </label>
            <div class="flex space-x-1">
              <button 
                @click="heatmapIntensity = Math.max(0.1, heatmapIntensity - 0.1)"
                class="text-xs bg-gray-100 w-5 h-5 rounded flex items-center justify-center"
              >-</button>
              <span class="text-xs text-gray-500">{{ Math.round(heatmapIntensity * 10) / 10 }}</span>
              <button 
                @click="heatmapIntensity = Math.min(2, heatmapIntensity + 0.1)"
                class="text-xs bg-gray-100 w-5 h-5 rounded flex items-center justify-center"
              >+</button>
            </div>
          </div>
          
          <div class="flex items-center justify-between">
            <label class="flex items-center">
              <input 
                v-model="layerVisibility.userUploads" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">User Uploads</span>
            </label>
            <span class="text-xs text-gray-500">{{ userUploadCount }} items</span>
          </div>
        </div>
      </div>
    </div>
    <!-- Measurement Panel -->
    <div 
      v-if="showMeasure"
      class="absolute top-4 right-16 z-10 w-72 transform transition-all duration-300"
      :class="{ 'translate-x-0 opacity-100': showMeasure, 'translate-x-8 opacity-0': !showMeasure }"
    >
      <div class="p-4 bg-white rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-4">Measurement Tools</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Measurement Type</label>
            <div class="flex space-x-2">
              <button 
                @click="measureType = 'distance'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="measureType === 'distance' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                <i class="fas fa-ruler-horizontal mr-1"></i> Distance
              </button>
              <button 
                @click="measureType = 'area'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="measureType === 'area' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                <i class="fas fa-draw-polygon mr-1"></i> Area
              </button>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Units</label>
            <select 
              v-model="measureUnits"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="metric">Metric (m/km²)</option>
              <option value="imperial">Imperial (ft/acres)</option>
            </select>
          </div>
          
          <div v-if="measureResult">
            <label class="block text-sm font-medium text-gray-700 mb-1">Measurement Result</label>
            <div class="bg-gray-50 p-2 rounded-md text-sm">
              <span class="font-medium">{{ measureResult.value }}</span>
              <span class="text-gray-500 ml-1">{{ measureResult.unit }}</span>
            </div>
          </div>
          
          <div class="flex justify-between">
            <button 
              @click="startMeasuring"
              class="bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors"
            >
              Start Measuring
            </button>
            <button 
              @click="clearMeasurement"
              class="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
              :disabled="!isMeasuring"
            >
              Clear
            </button>
          </div>
          
          <div class="text-xs text-gray-500">
            <p>Click on the map to start measuring. Double-click to finish.</p>
            <p>For area measurement, create a polygon by clicking multiple points.</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Export Map Panel -->
    <div 
      v-if="showExport"
      class="absolute top-4 right-16 z-10 w-72 transform transition-all duration-300"
      :class="{ 'translate-x-0 opacity-100': showExport, 'translate-x-8 opacity-0': !showExport }"
    >
      <div class="p-4 bg-white rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-4">Export Map</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
            <div class="flex space-x-2">
              <button 
                @click="exportFormat = 'png'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="exportFormat === 'png' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                PNG
              </button>
              <button 
                @click="exportFormat = 'jpeg'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="exportFormat === 'jpeg' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                JPEG
              </button>
              <button 
                @click="exportFormat = 'pdf'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="exportFormat === 'pdf' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                PDF
              </button>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Paper Size</label>
            <select 
              v-model="paperSize"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="a4">A4</option>
              <option value="letter">Letter</option>
              <option value="legal">Legal</option>
              <option value="tabloid">Tabloid/Ledger</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Orientation</label>
            <div class="flex space-x-2">
              <button 
                @click="orientation = 'portrait'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="orientation === 'portrait' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                <i class="fas fa-file-alt mr-1"></i> Portrait
              </button>
              <button 
                @click="orientation = 'landscape'"
                class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                :class="orientation === 'landscape' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                <i class="fas fa-file mr-1"></i> Landscape
              </button>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Resolution (DPI)</label>
            <input 
              v-model.number="resolution" 
              type="range" 
              min="72" 
              max="300" 
              step="1" 
              class="w-full"
            >
            <div class="flex justify-between text-xs text-gray-500">
              <span>72</span>
              <span>{{ resolution }}</span>
              <span>300</span>
            </div>
          </div>
          
          <div>
            <label class="flex items-center">
              <input 
                v-model="includeTitle" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">Include Title</span>
            </label>
            <input 
              v-if="includeTitle"
              v-model="mapTitle"
              type="text"
              placeholder="Map Title"
              class="mt-2 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
          </div>
          
          <div>
            <label class="flex items-center">
              <input 
                v-model="includeLegend" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">Include Legend</span>
            </label>
          </div>
          
          <div>
            <label class="flex items-center">
              <input 
                v-model="includeScaleBar" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm font-medium text-gray-700">Include Scale Bar</span>
            </label>
          </div>
          
          <button 
            @click="exportMap"
            class="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
          >
            Export Map
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import MapStyler from './MapStyler.vue';

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  propertyCount: {
    type: Number,
    default: 0
  },
  userUploadCount: {
    type: Number,
    default: 0
  },
  selectedProperties: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits([
  'toggle-layer', 
  'change-basemap', 
  'reset-view', 
  'toggle-selection',
  'style-applied',
  'style-created',
  'style-updated',
  'change-heatmap-intensity'
]);

// UI state
const showStyler = ref(false);
const showLayers = ref(false);
const showMeasure = ref(false);
const showExport = ref(false);
const isSelectionActive = ref(false);

// Measurement tools
const measureType = ref('distance');
const measureUnits = ref('metric');
const isMeasuring = ref(false);
const measureResult = ref(null);
const measureInteraction = ref(null);
const measureLayer = ref(null);

// Export Map options
const exportFormat = ref('png');
const paperSize = ref('a4');
const orientation = ref('landscape');
const resolution = ref(150);
const includeTitle = ref(true);
const mapTitle = ref('Property Map');
const includeLegend = ref(true);
const includeScaleBar = ref(true);

// Layer controls
const layerVisibility = ref({
  baseMap: true,
  properties: true,
  heatmap: false,
  userUploads: true
});

const baseMapType = ref('satellite');
const heatmapIntensity = ref(1.0);

// Watch for layer visibility changes
watch(layerVisibility, (newValue) => {
  Object.entries(newValue).forEach(([layer, visible]) => {
    emit('toggle-layer', { layer, visible });
  });
}, { deep: true });

// Watch for basemap changes
watch(baseMapType, (newValue) => {
  emit('change-basemap', newValue);
});

// Watch for heatmap intensity changes
watch(heatmapIntensity, (newValue) => {
  emit('change-heatmap-intensity', newValue);
});

// Methods
const resetView = () => {
  emit('reset-view');
};

const toggleSelection = () => {
  isSelectionActive.value = !isSelectionActive.value;
  emit('toggle-selection', isSelectionActive.value);
};

// Handle style events
const onStyleApplied = (data) => {
  emit('style-applied', data);
};

const onStyleCreated = (style) => {
  emit('style-created', style);
};

const onStyleUpdated = (style) => {
  emit('style-updated', style);
};

// Measurement functions
const startMeasuring = () => {
  if (isMeasuring.value) {
    clearMeasurement();
  }
  
  isMeasuring.value = true;
  
  // This would use OpenLayers Draw and Modify interactions
  // For a real implementation, you would:
  // 1. Create a vector source and layer for measurements
  // 2. Add draw interaction based on measureType (LineString for distance, Polygon for area)
  // 3. Add event listeners to calculate measurements as the user draws
  
  // Simulate a measurement for demo purposes
  setTimeout(() => {
    if (measureType.value === 'distance') {
      measureResult.value = {
        value: measureUnits.value === 'metric' ? '245.8' : '806.4',
        unit: measureUnits.value === 'metric' ? 'meters' : 'feet'
      };
    } else {
      measureResult.value = {
        value: measureUnits.value === 'metric' ? '12,450' : '3.07',
        unit: measureUnits.value === 'metric' ? 'sq. meters' : 'acres'
      };
    }
  }, 1000);
};

const clearMeasurement = () => {
  isMeasuring.value = false;
  measureResult.value = null;
  
  // This would remove the measurement interactions and features
};

// Export functions
const exportMap = () => {
  // In a real implementation, this would:
  // 1. Create a canvas representation of the map
  // 2. Add title, legend, and scale bar if requested
  // 3. Export in the requested format (PNG, JPEG, PDF)
  // 4. Offer download to the user
  
  // For demo purposes, show an alert
  alert(`Map export initiated with the following settings:
  - Format: ${exportFormat.value.toUpperCase()}
  - Paper size: ${paperSize.value.toUpperCase()}
  - Orientation: ${orientation.value}
  - Resolution: ${resolution.value} DPI
  - Title included: ${includeTitle.value ? 'Yes' : 'No'}
  - Legend included: ${includeLegend.value ? 'Yes' : 'No'}
  - Scale bar included: ${includeScaleBar.value ? 'Yes' : 'No'}`);
  
  // Would trigger download in real implementation
  setTimeout(() => {
    console.log('Map export completed');
  }, 1500);
};

// Settings functions
const onSettingsApplied = (settings) => {
  console.log('Settings applied:', settings);
  
  // In a real implementation, this would update the app's appearance
  // by modifying CSS variables, toggling classes, etc.
  
  // Close the settings panel after applying
  showSettings.value = false;
};
</script>